/**
 * Vehicle shapes as the client reads them. They mirror `packages/contracts/src/vehicles.ts` (owned
 * by the vehicles backend); every field arriving from the network is read defensively through the
 * parsers below, so a slightly different server payload degrades instead of crashing the scene.
 */

export interface VehicleModel {
  id: string;
  name: string;
  class: string;
  priceCredits: number;
  topSpeedKmh: number;
  accel: number;
  seats: number;
  asset: { pack: string; file: string; lod1?: string };
  colors: string[];
}

export type VehicleState = "parked" | "driving";

export interface VehiclePose {
  x: number;
  z: number;
  yawDeg: number;
}

export interface VehicleEntity {
  id: string;
  modelId: string;
  ownerUserId: string;
  color: string;
  state: VehicleState;
  pose: VehiclePose;
  driverUserId?: string;
  odometerM: number;
}

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};
const num = (v: unknown, d = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);

export function parseModel(raw: unknown): VehicleModel | null {
  const m = obj(raw);
  const asset = obj(m["asset"]);
  const id = str(m["id"]);
  if (!id || !str(asset["file"])) return null;
  const colors = Array.isArray(m["colors"])
    ? m["colors"].filter((c): c is string => typeof c === "string")
    : [];
  return {
    id,
    name: str(m["name"], id),
    class: str(m["class"], "car"),
    priceCredits: num(m["priceCredits"]),
    topSpeedKmh: Math.max(20, num(m["topSpeedKmh"], 120)),
    accel: num(m["accel"], 0.5),
    seats: num(m["seats"], 4),
    asset: {
      pack: str(asset["pack"], "quaternius-cars"),
      file: str(asset["file"]),
      ...(typeof asset["lod1"] === "string" ? { lod1: asset["lod1"] } : {}),
    },
    colors: colors.length > 0 ? colors : ["#f2f2f0"],
  };
}

export function parseVehicle(raw: unknown): VehicleEntity | null {
  const v = obj(raw);
  const pose = obj(v["pose"]);
  const id = str(v["id"]);
  if (!id) return null;
  return {
    id,
    modelId: str(v["modelId"]),
    ownerUserId: str(v["ownerUserId"]),
    color: str(v["color"], "#f2f2f0"),
    state: v["state"] === "driving" ? "driving" : "parked",
    pose: { x: num(pose["x"]), z: num(pose["z"]), yawDeg: num(pose["yawDeg"]) },
    ...(typeof v["driverUserId"] === "string" ? { driverUserId: v["driverUserId"] } : {}),
    odometerM: num(v["odometerM"]),
  };
}

export const degToRad = (d: number): number => (d * Math.PI) / 180;
export const radToDeg = (r: number): number => (r * 180) / Math.PI;

/** Wraps an angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  else if (r <= -Math.PI) r += Math.PI * 2;
  return r;
}
